import { _ } from "i18n";
import { ucFirst } from "features/discover/functions";
import type { ColumnConfig } from "../../types";
import { renderBoolean } from "../../cellRenderers";
import {
	DiscoverGamesColumnField,
	DISCOVER_GAMES_COL_WIDTH_BOOLEAN,
	DISCOVER_GAMES_COL_WIDTH_LABEL_SHORT,
	DISCOVER_GAMES_COL_WIDTH_OWNERS,
	DISCOVER_GAMES_COL_WIDTH_PROCESSING_LEVEL,
	DISCOVER_GAMES_COL_WIDTH_VIP,
} from "../constants";
import { parseDiscoverGameOwners } from "../rowHelpers";
import { DiscoverGamesIsVipDropdownCell } from "../cells/DiscoverGamesIsVipDropdownCell";
import type { DiscoverGamesColumnsParams, GameRow } from "../types";

export function hasVideoColumn(): ColumnConfig<GameRow> {
	return {
		key: DiscoverGamesColumnField.HasVideo,
		label: _("discover.gamesTable.column.hasVideo"),
		sortable: true,
		sortField: DiscoverGamesColumnField.HasVideo,
		minWidth: DISCOVER_GAMES_COL_WIDTH_BOOLEAN.min,
		maxWidth: DISCOVER_GAMES_COL_WIDTH_BOOLEAN.max,
		cell: (value) => renderBoolean(value as boolean),
		group: _("discover.gamesTable.group.media"),
		defaultVisible: true,
		requiredFields: [DiscoverGamesColumnField.HasVideo],
	};
}

export function isVipColumn(p: DiscoverGamesColumnsParams = {}): ColumnConfig<GameRow> {
	return {
		key: DiscoverGamesColumnField.IsVip,
		label: _("discover.gamesTable.column.vip"),
		sortable: true,
		sortField: DiscoverGamesColumnField.IsVip,
		minWidth: DISCOVER_GAMES_COL_WIDTH_VIP.min,
		maxWidth: DISCOVER_GAMES_COL_WIDTH_VIP.max,
		cell: (_value, row) => (
			<DiscoverGamesIsVipDropdownCell
				row={row}
				isUpdating={p.isClubMetadataUpdating}
				onChange={p.canEditVip ? p.onIsVipChange : undefined}
				canEditByPermission={p.canEditVip}
			/>
		),
		group: _("discover.gamesTable.group.status"),
		defaultVisible: true,
		requiredFields: [DiscoverGamesColumnField.IsVip],
	};
}

export function isHiddenColumn(): ColumnConfig<GameRow> {
	return {
		key: DiscoverGamesColumnField.IsHidden,
		label: _("discover.gamesTable.column.hidden"),
		sortable: true,
		sortField: DiscoverGamesColumnField.IsHidden,
		minWidth: DISCOVER_GAMES_COL_WIDTH_BOOLEAN.min,
		maxWidth: DISCOVER_GAMES_COL_WIDTH_BOOLEAN.max,
		cell: (value) => renderBoolean(value as boolean),
		group: _("discover.gamesTable.group.status"),
		defaultVisible: false,
		requiredFields: [DiscoverGamesColumnField.IsHidden],
	};
}

export function isHiddenForScoutColumn(): ColumnConfig<GameRow> {
	return {
		key: DiscoverGamesColumnField.IsHiddenForScout,
		label: _("discover.gamesTable.column.hiddenForScout"),
		sortable: true,
		sortField: DiscoverGamesColumnField.IsHiddenForScout,
		minWidth: DISCOVER_GAMES_COL_WIDTH_BOOLEAN.min,
		maxWidth: DISCOVER_GAMES_COL_WIDTH_BOOLEAN.max,
		cell: (value) => renderBoolean(value as boolean),
		group: _("discover.gamesTable.group.status"),
		defaultVisible: false,
		requiredFields: [DiscoverGamesColumnField.IsHiddenForScout],
	};
}

export function ownersColumn(): ColumnConfig<GameRow> {
	return {
		key: DiscoverGamesColumnField.Owners,
		label: _("discover.gamesTable.column.owners"),
		sortable: true,
		sortField: DiscoverGamesColumnField.Owners,
		minWidth: DISCOVER_GAMES_COL_WIDTH_OWNERS.min,
		maxWidth: DISCOVER_GAMES_COL_WIDTH_OWNERS.max,
		cell: (value) => {
			const owners = parseDiscoverGameOwners(value);
			if (owners.length === 0) {
				return "-";
			}
			return owners.map((o) => o.name).join(", ");
		},
		group: _("discover.gamesTable.group.status"),
		defaultVisible: true,
		requiredFields: [DiscoverGamesColumnField.Owners],
	};
}

export function isDiscoverColumn(): ColumnConfig<GameRow> {
	return {
		key: DiscoverGamesColumnField.IsDiscover,
		label: _("discover.gamesTable.column.isDiscover"),
		sortable: true,
		sortField: DiscoverGamesColumnField.IsDiscover,
		minWidth: DISCOVER_GAMES_COL_WIDTH_LABEL_SHORT.min,
		maxWidth: DISCOVER_GAMES_COL_WIDTH_LABEL_SHORT.max,
		cell: (value) => {
			if (value === true) {
				return _("discover.gamesTable.cellValue.discover");
			}
			if (value === false) {
				return _("discover.gamesTable.cellValue.elite");
			}
			return "-";
		},
		group: _("discover.gamesTable.group.status"),
		defaultVisible: true,
		requiredFields: [DiscoverGamesColumnField.IsDiscover],
	};
}

export function requestedProcessingLevelColumn(): ColumnConfig<GameRow> {
	return {
		key: DiscoverGamesColumnField.RequestedProcessingLevel,
		label: _("discover.gamesTable.column.processingLevel"),
		sortable: false,
		minWidth: DISCOVER_GAMES_COL_WIDTH_PROCESSING_LEVEL.min,
		maxWidth: DISCOVER_GAMES_COL_WIDTH_PROCESSING_LEVEL.max,
		cell: (value) => ucFirst(value as string | null) || "-",
		group: _("discover.gamesTable.group.analysis"),
		defaultVisible: true,
		requiredFields: [DiscoverGamesColumnField.RequestedProcessingLevel],
	};
}
